import { z } from 'zod';

export const GAME_ID = 'city-tour';

export const MINUTES_OPTIONS = [15, 20, 30] as const;
export const TURN_SECONDS_OPTIONS = [20, 30, 45] as const;
export const TEAM_COUNT_OPTIONS = [2, 3, 4] as const;
export const MAX_SOLO = 8;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 12;

/** Tahta animasyonu: sunucu sonucu bu süreler bitince açıklar. */
export const DICE_MS = 1100;
export const STEP_MS = 220;

export const START_MONEY = 1500;
export const PASS_START = 200;
export const TOUR_FEE = 100;
export const BAIL = 150;
export const JAIL_TURNS = 2;
export const FINAL_MINUTES = 5;
export const WIN_GROUPS = 3;
export const MAX_LEVEL = 4;
/** Kira katları: seviye 1 (1 ev) … 4 (otel). */
export const RENT_MULT = [0, 0.25, 0.6, 1.5, 4] as const;
export const UPGRADE_MULT = 0.6;
export const TAKEOVER_MULT = 2;
export const SELL_MULT = 0.5;

export const CUSTOM_CATEGORY = 'arkadas';
export const CATEGORY_NAMES: Record<string, string> = {
  hareket: 'Hareket',
  taklit: 'Taklit',
  dans: 'Dans',
  sarki: 'Şarkı',
  komik: 'Komik',
  cesaret: 'Cesaret',
  'cesur-yetiskin': 'Cesur (+18)',
  'ates-yetiskin': 'Ateşli (+18)',
  [CUSTOM_CATEGORY]: 'Arkadaş görevleri',
};
/** +18 paketler: varsayılan ayarlarda yoktur, oda sahibi bilerek açar. */
export const ADULT_CATEGORIES: readonly string[] = ['cesur-yetiskin', 'ates-yetiskin'];
export const isAdultCategory = (id: string): boolean => ADULT_CATEGORIES.includes(id);

export const settingsSchema = z.object({
  mode: z.enum(['solo', 'teams']),
  teamCount: z.number().int().min(2).max(4),
  /** Oyuncu kimlikleri; takımı seçilmemiş oyuncular başlarken en küçük takıma dağıtılır. */
  teams: z.array(z.array(z.string()).max(12)).max(4),
  minutes: z.number().int().min(1).max(30),
  turnSeconds: z.number().int().min(5).max(60),
  /** Kapalıysa şans kartları yalnızca oyun içi uygulanır. */
  realTasks: z.boolean(),
  categories: z.array(z.string().min(1).max(40)).max(30),
});
export type CityTourSettings = z.infer<typeof settingsSchema>;

export const defaultSettings: CityTourSettings = {
  mode: 'solo',
  teamCount: 2,
  teams: [],
  minutes: 20,
  turnSeconds: 30,
  realTasks: true,
  categories: ['hareket', 'taklit', 'dans', 'sarki', 'komik', 'cesaret', CUSTOM_CATEGORY],
};

export const customTaskSchema = z.object({
  text: z.string().trim().min(5, 'Görev en az 5 harf olmalı').max(120, 'Görev en fazla 120 harf olabilir'),
});

export interface CategoryInfo {
  id: string;
  name: string;
  count: number;
}

export const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pick'), character: z.string() }),
  z.object({ type: z.literal('roll') }),
  z.object({ type: z.literal('bail') }),
  z.object({ type: z.literal('buy') }),
  z.object({ type: z.literal('upgrade') }),
  z.object({ type: z.literal('takeover') }),
  /** Karşısındaki teklifi geçer (satın alma, yükseltme, devralma, tur). */
  z.object({ type: z.literal('pass') }),
  z.object({ type: z.literal('tour'), cell: z.number().int().min(0).max(23) }),
  z.object({ type: z.literal('applyCard') }),
  z.object({ type: z.literal('chooseTask') }),
  z.object({ type: z.literal('taskDone') }),
  z.object({ type: z.literal('taskQuit') }),
  z.object({ type: z.literal('vote'), done: z.boolean() }),
  z.object({ type: z.literal('sell'), cell: z.number().int().min(0).max(23) }),
  z.object({ type: z.literal('bankrupt') }),
]);
export type CityTourAction = z.infer<typeof actionSchema>;

// Tahta -----------------------------------------------------------------------------------------
export type CellKind = 'start' | 'rest' | 'tour' | 'jail' | 'chance' | 'tax' | 'city';
export interface BoardCell {
  name: string;
  kind: CellKind;
  group?: number;
  price?: number;
}
/** Renk grupları, tahta sırasıyla. */
export const GROUP_COLORS = ['#ff8a5b', '#b794ff', '#5ab8ff', '#7ed0c8', '#3ee0a8', '#ffd23f', '#ff6b6b'] as const;
const c = (name: string, group: number, price: number): BoardCell => ({ name, kind: 'city', group, price });
const chance: BoardCell = { name: 'Şans', kind: 'chance' };
const tax: BoardCell = { name: 'Vergi', kind: 'tax' };
export const BOARD: readonly BoardCell[] = [
  { name: 'Başla', kind: 'start' },
  c('Kars', 0, 120), c('Van', 0, 140), chance, c('Rize', 1, 160), c('Ordu', 1, 180),
  { name: 'Mola', kind: 'rest' },
  c('Konya', 2, 200), c('Bursa', 2, 220), chance, c('Sinop', 3, 240), c('Hatay', 3, 260),
  { name: 'Tur', kind: 'tour' },
  c('Ankara', 4, 300), c('Adana', 4, 320), tax, c('İzmir', 5, 360), c('Muğla', 5, 380),
  { name: 'Hapis', kind: 'jail' },
  chance, c('Antalya', 6, 440), c('Bodrum', 6, 480), tax, chance,
];
export const JAIL_CELL = 18;
export const round10 = (n: number): number => Math.round(n / 10) * 10;
export const upgradeCost = (price: number): number => round10(price * UPGRADE_MULT);
/** Şehre konan toplam para: satın alma + yükseltmeler. */
export const invested = (price: number, level: number): number => (level <= 0 ? 0 : price + (level - 1) * upgradeCost(price));
export const baseRent = (price: number, level: number): number => round10(price * RENT_MULT[level]!);

// Karakterler -----------------------------------------------------------------------------------
export const CHARACTERS = [
  { id: 'patron', name: 'Patron' },
  { id: 'amca', name: 'Kasketli amca' },
  { id: 'is-kadini', name: 'İş kadını' },
  { id: 'genc', name: 'Kapüşonlu genç' },
  { id: 'sporcu', name: 'Sporcu' },
  { id: 'asci', name: 'Aşçı' },
  { id: 'rockci', name: 'Rockçı kız' },
  { id: 'teyze', name: 'Teyze' },
] as const;
export type CharacterId = (typeof CHARACTERS)[number]['id'];
/** Piyonun ayağındaki disk ve şehir çatıları: oyuncu/takım rengi. */
export const UNIT_COLORS = ['#ffd23f', '#ff7ac6', '#5ab8ff', '#3ee0a8', '#ff8a5b', '#b794ff', '#ff6b6b', '#ffffff'] as const;

// Görünüm ---------------------------------------------------------------------------------------
export type CityTourPhase = 'pick' | 'play' | 'podium';
export type Stage = 'pick' | 'roll' | 'moving' | 'buy' | 'upgrade' | 'takeover' | 'tour' | 'chance' | 'task' | 'vote' | 'debt' | 'over';

export interface CellView extends BoardCell {
  i: number;
  /** Grup rengi; şehir değilse null. */
  color: string | null;
  owner: string | null;
  level: number;
  /** Şu anki kira (grup ve son dakika katları dahil); sahipsizse 0. */
  rent: number;
  /** Otel olunca kira (katlar hariç). */
  hotelRent: number;
  upgrade: number;
  sell: number;
}
export interface UnitView {
  id: string;
  name: string;
  members: string[];
  character: CharacterId | null;
  color: string;
  money: number;
  wealth: number;
  at: number;
  jail: number;
  bankrupt: boolean;
  shield: boolean;
  groups: number;
}
export interface MoveView {
  seq: number;
  unitId: string;
  from: number;
  /** Eksi ise geri gider. */
  steps: number;
  dice: [number, number] | null;
  /** Yürümeden ışınlanır (Başla'ya git, hapis). */
  teleport: boolean;
}
export interface CardView {
  id: string;
  text: string;
  good: boolean;
  /** Görev başarılı olursa ne olacağı. */
  reward: string;
}
export interface TaskView {
  text: string;
  category: string;
  seconds: number;
}
export type Prompt =
  | { kind: 'buy'; cell: number; cost: number; canAfford: boolean }
  | { kind: 'upgrade'; cell: number; cost: number; canAfford: boolean; nextRent: number }
  | { kind: 'takeover'; cell: number; cost: number; canAfford: boolean; from: string }
  | { kind: 'tour'; cost: number; canAfford: boolean }
  | { kind: 'debt'; amount: number; need: number; to: string | null };
export interface PodiumRow {
  unitId: string;
  wealth: number;
  money: number;
  cities: number;
  bankrupt: boolean;
  rank: number;
}
export type EndReason = 'time' | 'last' | 'groups' | 'host';

export interface CityTourView {
  phase: CityTourPhase;
  stage: Stage;
  serverNow: number;
  /** Aşamanın bitişi; 0 = süresiz. */
  endsAt: number;
  durationMs: number;
  gameEndsAt: number;
  /** Son dakikalar başladı: kiralar 2 kat. */
  final: boolean;
  finalAt: number;
  turnSeconds: number;
  cells: CellView[];
  units: UnitView[];
  activeUnitId: string | null;
  /** Şu an hamle yapacak oyuncu. */
  actorId: string | null;
  myUnitId: string | null;
  isActor: boolean;
  dice: [number, number] | null;
  move: MoveView | null;
  prompt: Prompt | null;
  card: CardView | null;
  task: TaskView | null;
  /** Bu kartta görev seçilebilir mi. */
  canTask: boolean;
  vote: { done: number; failed: number; pending: number; total: number; mine: boolean | null; canVote: boolean } | null;
  /** Son olaylar, yeniden eskiye. */
  feed: string[];
  characters: { id: CharacterId; name: string; takenBy: string | null }[];
  podium: { rows: PodiumRow[]; reason: EndReason; winners: string[] } | null;
}
